/*
  Variant-aware product media gallery.

  When the selected variant changes, decides (via the variant gallery map
  rendered by product-template.liquid) whether the gallery media set differs
  from the one currently shown. If it does, it re-fetches the section through
  the Section Rendering API, swaps the gallery wrapper, re-initialises Alpine
  and the splide slideshows, and points the Product component at the new
  gallery's first media item.
*/
(() => {
  if (window.__variantGalleryInitialized) return;
  window.__variantGalleryInitialized = true;

  let requestToken = 0;

  const BUSY_CLASS = 'variant-gallery-busy';
  const LOADING_CLASS = 'variant-gallery-loading';

  const stageOf = (wrapper) =>
    wrapper.querySelector('[data-variant-gallery-stage]');

  const setStageLoading = (wrapper, loading) => {
    const stage = stageOf(wrapper);
    if (!stage) return;
    stage.classList.toggle(LOADING_CLASS, loading);
  };

  // Resolves when the first visible gallery image has loaded (or after a
  // timeout, so the skeleton can never get stuck).
  const firstImageLoaded = (wrapper, timeout = 4000) =>
    new Promise((resolve) => {
      const stage = stageOf(wrapper);
      const img =
        stage?.querySelector('.feature-media-item img') ||
        stage?.querySelector('img');
      if (!img || (img.complete && img.naturalWidth > 0)) {
        resolve();
        return;
      }
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      setTimeout(done, timeout);
    });

  const setCurrentMediaId = (wrapper) => {
    if (!window.Alpine) return;
    const data = window.Alpine.$data(wrapper);
    const firstMediaId = wrapper.dataset.galleryFirstMedia;
    if (data && firstMediaId) {
      data.currentMediaId = firstMediaId;
    }
  };

  const swapGallery = async (wrapper, variantId) => {
    const token = ++requestToken;

    wrapper.classList.add(BUSY_CLASS);
    setStageLoading(wrapper, true);

    const url = new URL(wrapper.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', wrapper.dataset.sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      if (token !== requestToken) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-variant-media-gallery]');
      if (!next) throw new Error('Gallery markup missing in fetched section');

      const fresh = document.importNode(next, true);
      fresh.classList.add(BUSY_CLASS);
      setStageLoading(fresh, true);

      if (window.destroySlideshowsIn) window.destroySlideshowsIn(wrapper);
      wrapper.replaceWith(fresh);

      if (window.Alpine) window.Alpine.initTree(fresh);
      if (window.discoverNewSlideshows) window.discoverNewSlideshows(fresh);

      setCurrentMediaId(fresh);

      await firstImageLoaded(fresh);
      if (token !== requestToken) return;
      fresh.classList.remove(BUSY_CLASS);
      setStageLoading(fresh, false);
    } catch (error) {
      console.error('[variant-gallery] Failed to update gallery:', error);
      wrapper.classList.remove(BUSY_CLASS);
      setStageLoading(wrapper, false);
    }
  };

  document.addEventListener('shapes:product:variantchange', (event) => {
    const root = event.target;
    if (!(root instanceof Element)) return;

    const wrapper = root.querySelector('[data-variant-media-gallery]');
    if (!wrapper) return;

    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    let map = {};
    const mapEl = root.querySelector('[data-variant-gallery-map]');
    if (mapEl) {
      try {
        map = JSON.parse(mapEl.textContent);
      } catch (error) {
        console.error('[variant-gallery] Invalid gallery map JSON:', error);
      }
    }

    const targetKey = map[String(variant.id)] || 'default';
    if (targetKey === wrapper.dataset.galleryKey) {
      return;
    }

    swapGallery(wrapper, variant.id);
  });
})();
